import { UserUseCase } from "lib/models/userusecase-models";
import { ItemModel } from "lib/services/catalogical-models";
import { CatalogicalService } from "lib/services/catalogical-service";
import { SearchExperienceService } from "lib/services/shop-search-service";
import { useEffect, useMemo, useState } from "react";

const RELATED_LIMIT = 3;

export interface RelatedUseCaseItem {
  title: string;
  tool: string;
  used: number;
  shortDescription?: string;
  taxonomyId: string;
}

export function useRelatedUseCases(useCase: UserUseCase | null): { related: RelatedUseCaseItem[]; isLoading: boolean } {
  const [related, setRelated] = useState<RelatedUseCaseItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const service = useMemo(() => new SearchExperienceService("userusecase"), []);

  useEffect(() => {
    if (!useCase?.name) return;

    setIsLoading(true);
    const query = [useCase.name, useCase.categories?.join(" "), useCase.recommendedAudience?.join(" ")]
      .filter(Boolean)
      .join(" ");

    service
      .getItems<UserUseCase>({
        searchQuery: query,
        useSemanticSearch: true,
        filter: JSON.stringify({ status: ["PUBLISHED"] }),
        limit: RELATED_LIMIT + 1,
        offset: 0,
        excludeFields: "embeddings,steps",
      })
      .then(async (res) => {
        const currentId = useCase.taxonomyId;
        const items = (res.data ?? []).filter((item) => item.taxonomyId !== currentId).slice(0, RELATED_LIMIT);

        // Resolve tool names
        const toolIds = [...new Set(items.flatMap((item) => item.associatedToolIds ?? []))];
        const toolNames: Record<string, string> = {};
        if (toolIds.length) {
          try {
            const toolService = new CatalogicalService<ItemModel>("aiforme");
            const toolRes = await toolService.getItems({
              filters: { taxonomyId: toolIds },
              pageSize: toolIds.length,
              sortBy: [],
              sortDirection: [],
            });
            toolRes?.data?.forEach((t) => {
              if (t.taxonomyId && t.title) toolNames[t.taxonomyId] = t.title;
            });
          } catch {
            /* fallback below */
          }
        }

        setRelated(
          items.map((item) => {
            const firstToolId = item.associatedToolIds?.[0] ?? "";
            return {
              title: item.name,
              tool: toolNames[firstToolId] || firstToolId.split("/").pop()?.replace(/-/g, " ") || firstToolId,
              used: item.usedCount ?? 0,
              shortDescription: item.shortDescription,
              taxonomyId: item.taxonomyId,
            };
          })
        );
      })
      .catch(() => setRelated([]))
      .finally(() => setIsLoading(false));
  }, [useCase?.taxonomyId]);

  return { related, isLoading };
}
